import { useEffect } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { ApiError } from "../api/client";
import { useActiveTenant, useMyTenants } from "../api/hooks";

// Sends an authenticated user who belongs to NO workspace to /onboarding,
// where they create their free tenant. Wraps <Outlet/> inside RootLayout so it
// intercepts before dashboard widgets render tenant-scoped queries against a
// missing tenant.
//
// Loop/edge safety:
//   - On any /onboarding path → render children directly (never redirect onto
//     itself — the primary loop guard).
//   - "No workspace" requires BOTH signals: useActiveTenant() 404 AND
//     useMyTenants() empty. This avoids the race right after tenant creation
//     where the active-tenant key (keyed by activeTenantId, briefly null) still
//     404s while membership already exists.
//   - Never redirect while either query is pending (show a loader).
//   - A non-404 error (transient 5xx) shows an error, never loops to onboarding.
//   - Platform admins always have a non-empty useMyTenants() → never redirected.
export function OnboardingGuard({ children }: { children: React.ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const tenant = useActiveTenant();
  const mine = useMyTenants();

  const onOnboarding = pathname.startsWith("/onboarding");
  const tenant404 =
    tenant.isError && tenant.error instanceof ApiError && tenant.error.status === 404;
  const noWorkspace = tenant404 && mine.isSuccess && (mine.data?.length ?? 0) === 0;

  useEffect(() => {
    if (!onOnboarding && noWorkspace) {
      navigate({ to: "/onboarding" });
    }
  }, [onOnboarding, noWorkspace, navigate]);

  if (onOnboarding) return <>{children}</>;

  if (tenant.isPending || mine.isPending) {
    return <Centered>Carregando…</Centered>;
  }
  if (tenant.isError && !tenant404) {
    return <Centered>Não foi possível carregar sua workspace. Tente recarregar a página.</Centered>;
  }
  if (noWorkspace) {
    return <Centered>Redirecionando para a criação da workspace…</Centered>;
  }
  return <>{children}</>;
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-[50vh] items-center justify-center text-sm text-slate-500 dark:text-slate-400">
      {children}
    </div>
  );
}
